import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import './TimesheetPage.css'; // Reuse Timesheet layout classes for tables/modals

function FinanceApprovalsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('APPROVE'); // 'APPROVE' or 'REJECT'
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/financial-transactions/pending');
      setTransactions(res.data?.data || []);
    } catch (err) {
      toast.error('Failed to fetch pending approvals');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getModuleLabel = (module) => {
    switch (module) {
      case 'INCOME': return 'Customer Receipt';
      case 'EXPENSE': return 'Expense';
      case 'PURCHASE': return 'Vendor Purchase';
      case 'PAYMENT': return 'Vendor Payment';
      case 'ADVANCE': return 'Cash Advance';
      default: return module;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING_L1': return 'Pending L1 Approval';
      case 'PENDING_L2': return 'Pending L2 Approval';
      case 'APPROVED': return 'Approved';
      case 'REJECTED': return 'Rejected';
      default: return status;
    }
  };

  const handleActionClick = (txn, type) => {
    setSelectedTxn(txn);
    setActionType(type);
    setRemarks('');
    setShowActionModal(true);
  };

  const submitAction = async () => {
    if (actionType === 'REJECT' && !remarks.trim()) {
      toast.error('Rejection remarks are mandatory');
      return;
    }

    setProcessing(true);
    try {
      const url = `/financial-transactions/${selectedTxn.id}/${actionType === 'APPROVE' ? 'approve' : 'reject'}`;
      await axios.post(url, { remarks: remarks.trim() });
      toast.success(`Transaction ${actionType === 'APPROVE' ? 'approved' : 'rejected'} successfully`);
      setShowActionModal(false);
      setShowDetail(false);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const fmtDate = (dStr) => {
    if (!dStr) return '';
    const d = new Date(dStr);
    return d.toLocaleDateString();
  };

  const fmtCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(val || 0);
  };

  if (loading) {
    return (
      <div className="settings-page loading">
        <div className="spinner">Loading Pending Approvals...</div>
      </div>
    );
  }

  return (
    <div className="timesheet-page" style={{ padding: '20px' }}>
      <div className="timesheet-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2>Finance Approvals Queue</h2>
          <p>Review and action pending Level 1 & Level 2 approvals assigned to your role.</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchPending}>🔄 Refresh</button>
      </div>

      <div className="timesheet-table-container">
        {transactions.length === 0 ? (
          <div className="settings-empty" style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>✅</div>
            <h3>Approvals Inbox Empty</h3>
            <p>There are no transactions currently awaiting approval for your role.</p>
          </div>
        ) : (
          <table className="timesheet-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                <th style={{ padding: '12px' }}>Date</th>
                <th style={{ padding: '12px' }}>Voucher No</th>
                <th style={{ padding: '12px' }}>Module Type</th>
                <th style={{ padding: '12px' }}>Amount</th>
                <th style={{ padding: '12px' }}>Submitted By</th>
                <th style={{ padding: '12px' }}>Current Status</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(txn => (
                <tr key={txn.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '12px' }}>{fmtDate(txn.transactionDate)}</td>
                  <td style={{ padding: '12px', fontWeight: 'bold' }}>{txn.voucherNo}</td>
                  <td style={{ padding: '12px' }}>{getModuleLabel(txn.transactionType)}</td>
                  <td style={{ padding: '12px', fontWeight: '600' }}>{fmtCurrency(txn.amount)}</td>
                  <td style={{ padding: '12px' }}>{txn.createdByName || 'System'}</td>
                  <td style={{ padding: '12px' }}>
                    <span className={`status-badge ${txn.status === 'PENDING_L1' ? 'status-draft' : 'status-submitted'}`} style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                      {getStatusLabel(txn.status)}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedTxn(txn); setShowDetail(true); }}>
                      👁️ View
                    </button>
                    <button className="btn btn-success btn-sm" onClick={() => handleActionClick(txn, 'APPROVE')} style={{ backgroundColor: 'var(--success-color)', color: 'white' }}>
                      ✓ Approve
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleActionClick(txn, 'REJECT')} style={{ backgroundColor: 'var(--danger-color)', color: 'white' }}>
                      ✗ Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Transaction Details Modal */}
      {showDetail && selectedTxn && (
        <div className="modal-backdrop">
          <div className="ts-modal card" style={{ maxWidth: '600px', width: '100%', padding: '20px' }}>
            <div className="ts-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Transaction Details - {selectedTxn.voucherNo}</h3>
              <button className="close-btn" onClick={() => setShowDetail(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <div className="ts-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Transaction Date</label>
                  <div style={{ fontWeight: '500' }}>{fmtDate(selectedTxn.transactionDate)}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Voucher Type</label>
                  <div style={{ fontWeight: '500' }}>{getModuleLabel(selectedTxn.transactionType)}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Amount</label>
                  <div style={{ fontWeight: '600', color: 'var(--primary-color)' }}>{fmtCurrency(selectedTxn.amount)}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Payment Mode</label>
                  <div style={{ fontWeight: '500' }}>{selectedTxn.paymentMode || 'N/A'}</div>
                </div>
              </div>

              {selectedTxn.projectName && (
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Project</label>
                  <div style={{ fontWeight: '500' }}>{selectedTxn.projectName}</div>
                </div>
              )}

              {selectedTxn.vendorName && (
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Vendor</label>
                  <div style={{ fontWeight: '500' }}>{selectedTxn.vendorName}</div>
                </div>
              )}

              {selectedTxn.customerName && (
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Customer</label>
                  <div style={{ fontWeight: '500' }}>{selectedTxn.customerName}</div>
                </div>
              )}

              {selectedTxn.accountHeadName && (
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Account Head</label>
                  <div style={{ fontWeight: '500' }}>{selectedTxn.accountHeadName}</div>
                </div>
              )}

              {selectedTxn.paymentAccountHeadName && (
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Payment / Asset Account</label>
                  <div style={{ fontWeight: '500' }}>{selectedTxn.paymentAccountHeadName}</div>
                </div>
              )}

              {selectedTxn.notes && (
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Notes / Description</label>
                  <div style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '14px' }}>
                    {selectedTxn.notes}
                  </div>
                </div>
              )}
            </div>

            <div className="ts-modal-footer" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '15px', marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn btn-secondary" onClick={() => setShowDetail(false)}>Close</button>
              <button className="btn btn-danger" onClick={() => handleActionClick(selectedTxn, 'REJECT')} style={{ backgroundColor: 'var(--danger-color)', color: 'white' }}>
                ✗ Reject
              </button>
              <button className="btn btn-success" onClick={() => handleActionClick(selectedTxn, 'APPROVE')} style={{ backgroundColor: 'var(--success-color)', color: 'white' }}>
                ✓ Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Dialog (Approve / Reject) Modal */}
      {showActionModal && selectedTxn && (
        <div className="modal-backdrop">
          <div className="ts-modal card" style={{ maxWidth: '450px', width: '100%', padding: '20px' }}>
            <div className="ts-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>
                {actionType === 'APPROVE' ? 'Confirm Approval' : 'Provide Rejection Remarks'}
              </h3>
              <button className="close-btn" onClick={() => setShowActionModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <div className="ts-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Are you sure you want to <strong>{actionType === 'APPROVE' ? 'approve' : 'reject'}</strong> transaction{' '}
                <strong>{selectedTxn.voucherNo}</strong> of amount <strong>{fmtCurrency(selectedTxn.amount)}</strong>?
              </p>
              <div className="form-group">
                <label>Remarks {actionType === 'REJECT' && <span className="required-star">*</span>}</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={actionType === 'APPROVE' ? 'Approval remarks (optional)...' : 'Mandatory rejection remarks...'}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>
            <div className="ts-modal-footer" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '15px', marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn btn-secondary" onClick={() => setShowActionModal(false)} disabled={processing}>Cancel</button>
              <button
                className={`btn ${actionType === 'APPROVE' ? 'btn-success' : 'btn-danger'}`}
                onClick={submitAction}
                disabled={processing}
                style={{ backgroundColor: actionType === 'APPROVE' ? 'var(--success-color)' : 'var(--danger-color)', color: 'white' }}
              >
                {processing ? 'Processing...' : actionType === 'APPROVE' ? '✓ Approve' : '✗ Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FinanceApprovalsPage;
